import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, dictionaries, policies, type DictionaryDetail, type DictionarySummary } from '../../lib/api'

interface Props {
  policyId: string | null
  /**
   * Dictionaries chosen before the draft has been saved. There is no policy to record a binding
   * against yet, so the page holds them and binds them in the same act as the first save, rather than
   * sending the operator away to save and come back.
   */
  pending: string[]
  onPendingChange: (ids: string[]) => void
  /** Reported up so the page can say when a prompt watcher has nothing to look for. */
  onBoundCountChange: (count: number) => void
  maximumContentRules: number
  maximumSignalRules: number
  /** Rules written into the document itself, which share the agent's ceiling with the bound ones. */
  inlineContentRules: number
  inlineSignalRules: number
}

/** Never published arrives as an absent member rather than as null, so both have to be handled. */
function publishedRevision(entry: DictionarySummary): number | null {
  return entry.publishedRevision ?? null
}

/**
 * The dictionaries this policy carries.
 *
 * Bound by reference, never copied. A dictionary is edited on its own page, by whoever owns the words
 * — which is often not the person who owns the policy — and publication resolves each binding against
 * whatever revision of that dictionary is published at that moment. That is the whole point of the
 * indirection: correcting "recieving bank" reaches the fleet by republishing the policies that bind
 * the dictionary, rather than by opening and editing every one of them.
 *
 * The bound set is read by identity rather than filtered out of a page of results. A tenant can hold
 * hundreds of dictionaries, and a bound one that happened to fall on page four would show as unbound
 * — which is the one mistake this screen must never make, because the operator would bind it twice or
 * conclude the policy carries nothing.
 *
 * Bindings save the moment they change and deliberately do not republish. "What this policy carries"
 * and "when the fleet gets it" are two separate operator decisions, and collapsing them would mean
 * every button here pushed to the whole estate.
 */
export function DictionaryBindings({
  policyId,
  pending,
  onPendingChange,
  onBoundCountChange,
  maximumContentRules,
  maximumSignalRules,
  inlineContentRules,
  inlineSignalRules
}: Props) {
  const [chosen, setChosen] = useState<DictionarySummary[]>([])
  const [matches, setMatches] = useState<DictionarySummary[] | null>(null)
  const [total, setTotal] = useState(0)
  const [search, setSearch] = useState('')
  const [failure, setFailure] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const loadBound = useCallback(async () => {
    const ids = policyId ? await policies.bindings(policyId) : pending
    setChosen(ids.length === 0 ? [] : (await dictionaries.list({ ids, limit: ids.length })).items)
  }, [policyId, pending])

  useEffect(() => {
    onBoundCountChange(chosen.length)
  }, [chosen.length, onBoundCountChange])

  useEffect(() => {
    loadBound().catch((error: unknown) =>
      setFailure(error instanceof ApiError ? error.message : 'The bound dictionaries could not be read.')
    )
  }, [loadBound])

  // Debounced, like the editor's own validation: a tenant with hundreds of dictionaries would
  // otherwise issue one query per keystroke.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      dictionaries
        .list({ search: search || undefined, limit: 15 })
        .then((page) => {
          setMatches(page.items)
          setTotal(page.total)
        })
        .catch(() => {
          /* A failed search must not blank the bound list, which is the part that matters. */
        })
    }, 300)
    return () => window.clearTimeout(timer)
  }, [search])

  const rebind = async (next: string[], describe: string) => {
    if (!policyId) {
      // Held, not saved: loadBound re-reads the chosen set from the new list.
      onPendingChange(next)
      setNotice('Held on this page. It is recorded when this draft is first saved.')
      return
    }
    setBusy(true)
    setFailure(null)
    setNotice(null)
    try {
      await policies.bind(policyId, next)
      await loadBound()
      setNotice(describe)
    } catch (error) {
      setFailure(error instanceof ApiError ? error.message : 'The binding could not be saved.')
      await loadBound()
    } finally {
      setBusy(false)
    }
  }

  const boundIds = new Set(chosen.map((entry) => entry.dictionaryId))
  const contentTotal = inlineContentRules + chosen.reduce((sum, entry) => sum + entry.contentRuleCount, 0)
  const signalTotal = inlineSignalRules + chosen.reduce((sum, entry) => sum + entry.signalRuleCount, 0)
  const unpublished = chosen.filter((entry) => publishedRevision(entry) === null)
  const overCeiling = contentTotal > maximumContentRules || signalTotal > maximumSignalRules
  const unbound = (matches ?? []).filter((entry) => !boundIds.has(entry.dictionaryId))

  return (
    <div data-testid="dictionary-bindings">
      <p className="hint">
        A dictionary is edited on <Link to="/policies?tab=dictionaries">its own page</Link> and bound here.
        Publishing merges each bound dictionary's published revision into this policy, so correcting a
        term reaches the fleet by republishing the policies that bind it rather than by editing them.
      </p>

      {failure && (
        <div className="banner error" role="alert">
          {failure}
        </div>
      )}
      {notice && (
        <div className="banner success" role="status" data-testid="binding-notice">
          {notice}
        </div>
      )}

      <h4>Bound to this policy</h4>
      {chosen.length === 0 ? (
        <p className="hint" data-testid="no-bindings">
          None yet, so this policy checks prompts against nothing. Bind one below — every tenant starts
          with the collections Coastline ships.
        </p>
      ) : (
        <ul className="binding-list" data-testid="bound-dictionaries">
          {chosen.map((entry) => (
            <li key={entry.dictionaryId}>
              <Row entry={entry} />
              <div className="toolbar">
                <Link className="button" to={`/dictionaries/${entry.dictionaryId}`}>
                  Edit
                </Link>
                <button
                  type="button"
                  disabled={busy}
                  onClick={() =>
                    void rebind(
                      chosen.filter((one) => one.dictionaryId !== entry.dictionaryId).map((one) => one.dictionaryId),
                      `'${entry.name}' is no longer bound. It still exists; this policy just does not carry it.`
                    )
                  }
                  data-testid={`unbind-${entry.dictionaryId}`}
                >
                  Unbind
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      <p className="hint" data-testid="binding-total">
        Once merged: {contentTotal} of {maximumContentRules} content rules, {signalTotal} of{' '}
        {maximumSignalRules} signal rules.
        {inlineContentRules + inlineSignalRules > 0 &&
          ` That includes ${inlineContentRules + inlineSignalRules} written into this policy itself.`}
      </p>

      {overCeiling && (
        <div className="banner error" data-testid="binding-over-ceiling">
          That is past what the agent accepts, so publication will be refused. Unbind one, or split the
          rules across two policies.
        </div>
      )}

      {unpublished.length > 0 && (
        <div className="banner warning" data-testid="binding-unpublished">
          {unpublished.length === 1
            ? `'${unpublished[0]!.name}' has no published revision.`
            : `${unpublished.length} bound dictionaries have no published revision.`}{' '}
          Publishing this policy is refused until they are published, rather than quietly leaving their
          rules out.
        </div>
      )}

      <h4>Bind another</h4>
      <input
        type="search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search dictionaries"
        aria-label="Search dictionaries"
        data-testid="search-dictionaries"
      />

      {unbound.length === 0 ? (
        <p className="hint" data-testid="no-dictionaries">
          {matches === null
            ? 'Loading…'
            : search
              ? 'Nothing matches that.'
              : 'Nothing else to bind.'}
        </p>
      ) : (
        <ul className="binding-list">
          {unbound.map((entry) => (
            <li key={entry.dictionaryId}>
              <Row entry={entry} />
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  void rebind(
                    [...chosen.map((one) => one.dictionaryId), entry.dictionaryId],
                    `'${entry.name}' is bound. It reaches the fleet the next time this policy is published.`
                  )
                }
                data-testid={`bind-${entry.dictionaryId}`}
              >
                Bind
              </button>
            </li>
          ))}
        </ul>
      )}

      {total > (matches?.length ?? 0) && (
        <p className="hint">
          Showing {matches?.length ?? 0} of {total}. Search to narrow it.
        </p>
      )}

      <p className="hint" data-testid={policyId ? undefined : 'bindings-held'}>
        {policyId
          ? 'Bindings save as you change them and take effect at the next publish. Changing one pushes nothing to the fleet on its own.'
          : 'This draft is not saved yet, so these are held on this page and recorded when it is. Leaving without saving drops them.'}
      </p>
    </div>
  )
}

/**
 * One dictionary, with its rules readable without leaving the page.
 *
 * "Eleven content rules" is not enough to decide whether to bind something to a policy that blocks:
 * the question is always which words. The rules load on expand rather than with the list, because a
 * page of fifteen dictionaries would otherwise fetch fifteen revisions to show counts nobody opened.
 */
function Row({ entry }: { entry: DictionarySummary }) {
  const published = publishedRevision(entry)
  const [detail, setDetail] = useState<DictionaryDetail | null>(null)
  const [failure, setFailure] = useState<string | null>(null)

  const load = () => {
    if (detail) return
    dictionaries
      .get(entry.dictionaryId)
      .then(setDetail)
      .catch((error: unknown) =>
        setFailure(error instanceof ApiError ? error.message : 'Its rules could not be read.')
      )
  }

  return (
    <span className="binding-row">
      <span className="binding-name">{entry.name}</span>
      <span className="hint">
        {published === null ? (
          <span className="warn">No published revision, so it would contribute nothing.</span>
        ) : (
          <>
            Published revision {published} · {entry.contentRuleCount} content, {entry.signalRuleCount} signal
            {entry.latestRevision > published &&
              ` · revision ${entry.latestRevision} is drafted and is not sent until it is published`}
          </>
        )}
      </span>

      <details className="rule-preview" onToggle={load} data-testid={`view-${entry.dictionaryId}`}>
        <summary>What is in it</summary>
        {failure && <span className="hint warn">{failure}</span>}
        {!detail && !failure && <span className="hint">Loading…</span>}
        {detail && (
          <ul>
            {detail.contentRules.map((rule) => (
              <li key={rule.name}>
                <span className={`pill sev-${severityRank(rule.severity)}`}>{rule.severity}</span>{' '}
                <strong>{rule.name}</strong>
                <span className="hint">{rule.matchAny.join(', ')}</span>
              </li>
            ))}
            {detail.signalRules.map((rule) => (
              <li key={rule.name}>
                <span className="pill">signal</span> <strong>{rule.name}</strong>
                <span className="hint">{rule.matchAny.join(', ')}</span>
              </li>
            ))}
          </ul>
        )}
      </details>
    </span>
  )
}

function severityRank(severity: string): number {
  switch (severity) {
    case 'low':
      return 2
    case 'medium':
      return 3
    case 'high':
      return 4
    case 'critical':
      return 5
    default:
      return 1
  }
}
